import type { PmQueueRow as RpcPmQueueRow } from '@kalos/kalos-rpc';

export const PAGE_VIEWS = ['contracts', 'pm-schedule'] as const;
export type PageView = (typeof PAGE_VIEWS)[number];

export const PAGE_VIEW_LABELS: Record<PageView, string> = {
  contracts: 'Contracts',
  'pm-schedule': 'PM Schedule',
};

export const PM_SECTIONS = ['queue', 'schedule', 'orders'] as const;
export type PmSection = (typeof PM_SECTIONS)[number];

export const PM_SECTION_LABELS: Record<PmSection, string> = {
  queue: 'Work Queue',
  schedule: 'Full Schedule',
  orders: 'Filter Orders',
};

export const DUE_WINDOWS = [
  'all',
  'any_status',
  'first_calls',
  'welcome_calls',
  'waitlist',
  'past_due',
  'missed',
  'month',
  'quarter',
  'next_3_months',
] as const;
export type DueWindow = (typeof DUE_WINDOWS)[number];

export const ORDER_TABS = ['all', 'scheduled', 'delivery'] as const;
export type OrderTab = (typeof ORDER_TABS)[number];

export const FILTER_ORDER_STATUSES = ['needs_ordered', 'ordered', 'delivered', 'canceled'] as const;
export type FilterOrderStatus = (typeof FILTER_ORDER_STATUSES)[number];

// Worklist status label + badge color, in one place.
export const FILTER_ORDER_STATUS: Record<FilterOrderStatus, { label: string; badge: string }> = {
  needs_ordered: { label: 'Needs Ordered', badge: 'bg-rose-50 text-rose-800 border-rose-200' },
  ordered: { label: 'Ordered', badge: 'bg-sky-50 text-sky-800 border-sky-200' },
  delivered: { label: 'Delivered', badge: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  // Muted, not destructive: a called-off order is a resolved state, not an alarm.
  canceled: { label: 'Canceled', badge: 'text-muted-foreground border-border bg-muted' },
};

// What each status means, shown on the worklist section headers.
export const FILTER_ORDER_STATUS_HINTS: Record<FilterOrderStatus, string> = {
  needs_ordered: 'Filters still to be ordered from the vendor',
  ordered:
    'Ordered; mark Delivered once the tech brings them to the visit or they reach the customer',
  delivered: 'Filters are with the customer or installed',
  canceled: 'Order withdrawn; a cancelled order can be deleted',
};

// Delivered and canceled are terminal, so neither has an entry and their rows render no
// action button.
export const NEXT_ORDER_ACTION: Partial<
  Record<FilterOrderStatus, { to: FilterOrderStatus; label: string }>
> = {
  needs_ordered: { to: 'ordered', label: 'Mark Ordered' },
  ordered: { to: 'delivered', label: 'Mark Delivered' },
};

export const DUE_WINDOW_LABELS: Record<DueWindow, string> = {
  all: 'All pending',
  any_status: 'All visits (any status)',
  first_calls: 'First Calls only',
  welcome_calls: 'Welcome Calls only',
  waitlist: 'Waitlist',
  past_due: 'Past due',
  missed: 'Missed (confirmed, not completed)',
  month: 'Due this month',
  quarter: 'Due this quarter',
  next_3_months: 'Due in 3 months',
};

// Work Queue sortable columns → server order_by keys (BatchGetPmQueue whitelist).
export const PM_SORT_KEYS = [
  'last_pm',
  'next_due',
  'customer_name',
  'property_address',
  'log_jobStatus',
  'assigned_csr_name',
  'last_activity',
  // Grouped view only: customers ordered by their earliest Last PM, first calls on top.
  'customer_last_pm',
] as const;
export type PmSortKey = (typeof PM_SORT_KEYS)[number];
export type PmSortDir = 'asc' | 'desc';

export type PmQueueSearch = {
  pm_q?: string;
  pm_due: DueWindow;
  pm_csr: number;
  pm_unassigned: boolean;
  pm_sector: 'all' | 'residential' | 'commercial';
  pm_dept: number;
  pm_sort?: PmSortKey;
  pm_dir?: PmSortDir;
  pm_group?: boolean;
};

// Full Schedule sortable columns → server order_by keys (BatchGetPmQueue whitelist).
export const PM_SCHEDULE_SORT_KEYS = ['next_due', 'customer_name', 'property_address'] as const;
export type PmScheduleSortKey = (typeof PM_SCHEDULE_SORT_KEYS)[number];

// Full Schedule date window presets (visit date_started bounds). The default, plan_year, is the
// last 12 months plus everything upcoming, so an open visit whose date has passed stays visible.
export const PM_WINDOWS = ['plan_year', 'upcoming', '7d', '30d', 'month', 'all'] as const;
export type PmWindow = (typeof PM_WINDOWS)[number];

// groups.group_id for Vacation Rental Manager.
export const VACATION_RENTAL_MANAGER_GROUP_ID = 7;

export const PM_WINDOW_LABELS: Record<PmWindow, string> = {
  plan_year: 'Plan year',
  upcoming: 'Upcoming',
  '7d': 'Next 7 days',
  '30d': 'Next 30 days',
  month: 'This month',
  all: 'All dates',
};

export type PmScheduleSearch = {
  pm_q?: string;
  pm_sector: 'all' | 'residential' | 'commercial';
  pm_dept: number;
  pm_status: string;
  pm_window: PmWindow;
  pm_vrm: boolean;
  pm_fs_sort?: PmScheduleSortKey;
  pm_fs_dir?: PmSortDir;
};

export type PmQueueRow = RpcPmQueueRow;

/** One visit cell in the Full Schedule grid. */
export type PlanVisitCell = {
  eventId: number;
  jobNumber: string;
  date: string;
  status: string;
  /** event.isResidential: 1 = residential, 0 = commercial. */
  isResidential: number;
  // open status, dated before today
  overdue: boolean;
  /** Membership benefit job (e.g. Water Heater): listed, but outside the PM cadence. */
  isBenefit: boolean;
};

/** One property row in the Full Schedule grid. */
export type PropertyScheduleRow = {
  propertyId: number;
  customerId: number;
  customerLabel: string;
  propertyLabel: string;
  propertyCity: string;
  cadenceLabel: string;
  nextPmDue: string; // cadence-due date of the earliest open visit (YYYY-MM-DD); '' when it has no predecessor
  nextPmOverdue: boolean;
  /** The visit the Next PM date belongs to; null when no open visit is a candidate. */
  nextPmVisit: PlanVisitCell | null;
  visits: PlanVisitCell[];
  manageEventId: number;
};

/** Customer group header + its property rows (Full Schedule grouping). */
export type CustomerScheduleGroup = {
  customerId: number;
  customerLabel: string;
  properties: PropertyScheduleRow[];
};
